import { Component, EventEmitter, Input, Output, inject, signal, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule, NgbModal, NgbModalConfig, NgbModalModule, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { HasRolesDirective } from 'keycloak-angular';
import { TranslateModule } from '@ngx-translate/core';
import { Backend } from 'src/app/backend-interface/backend';
import { Client, ClientApplication, Policy, RequestSimulation } from 'src/app/service/global.service';
import { TranslationService } from 'src/app/service/translation.service';
import { MatSliderModule } from '@angular/material/slider';
import { DatepickerComponent } from 'src/app/components/datepicker/datepicker.component';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import Keycloak from 'keycloak-js';
import { SimulationResultsComponent } from './simulation-results/simulation-results.component';
import { FormPolicyComponent } from '../form-policy/form-policy.component';

@Component({
  selector: 'app-form-add-policy-and-simulation',
  templateUrl: './form-add-policy-and-simulation.component.html',
  styleUrls: ['./form-add-policy-and-simulation.component.scss'],
  standalone: true,
  imports: [
    HasRolesDirective,
    TranslateModule, 
    ReactiveFormsModule,
    NgbDropdownModule,
    MatSliderModule,
    DatepickerComponent,
    CommonModule,
    MatCheckboxModule,
    FormsModule,
    SimulationResultsComponent,
    FormPolicyComponent,
    NgbModalModule
  ],
  providers: [TranslationService, NgbModal, NgbModalConfig, NgbActiveModal]
})
export class FormAddPolicyAndSimulationComponent implements OnInit {

  @Input() addpolicy?: boolean;
  @Input() policy?: Policy;
  @Input() lpolicies?: Policy[];
  @Input() lallPolicies?: Policy[];
  @Input() disabledB?: boolean;

  @Output() addPolicy = new EventEmitter<{ policy: Policy, toast: string }>();
  @Output() action = new EventEmitter<string>();

  readonly lbwversion = signal<any[]>([]);
  readonly lApplications = signal<ClientApplication[]>([]);
  readonly lclients = signal<Client[]>([]);
  readonly listPoliciesClientSelected = signal<Policy[]>([]);
  readonly resultSimulation = signal<any>(undefined);

  nameClient?: string;
  form = new UntypedFormGroup({});
  newPolicy?: Policy;

  private modalrefFullScreen?: NgbModalRef;
  private modalrefcenteredScreen?: NgbModalRef;

  currentdate = new Date(this.today());
  min_date = new Date(this.currentdate.getFullYear(), this.currentdate.getMonth() - 2, 0);
  number_value = 0;
  range_disabled = true;

  readonly translationService = inject(TranslationService);
  readonly modalService = inject(NgbModal);
  readonly keycloak = inject(Keycloak);

  ngOnInit(): void {
    if (this.policy || this.lallPolicies) {
      this.addpolicy = false;
    } else {
      this.addpolicy = true;
    }
  }

  open({ content }: { content: any }) {
   
    this.min_date.setDate(Math.min(this.currentdate.getDate(), this.min_date.getDate()));
    this.ngOnInit();
    this.modalrefcenteredScreen = this.modalService.open(content, { centered: true });
    this.initForm();
  }

  sendAction(action: string) {
    this.action.emit(action);
  }

  async getBWVersions(os: string | undefined) {
    this.lbwversion.set(await Backend.POLICIES.getBWV(os));
  }

  getAllOS() {
    return ['Android', 'iOS'];
  }

  getControl(cat: string) {
    return this.form.get(cat) as UntypedFormControl;
  }

  async initForm() {
    if (this.addpolicy) {
      this.form.addControl('policy-name', new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(5),
        Backend.SETTINGS.noWhitespaceValidator(),
        Backend.POLICIES.notExistValidator(this.lallPolicies)
      ]));
      this.form.addControl('os', new UntypedFormControl('Android'));
      await this.getBWVersions(this.form.value['os'].toLowerCase());
      this.form.addControl('BWVersion', new UntypedFormControl(this.lbwversion()?.[0]?.version));
      if (this.keycloak.hasRealmRole('bo-dip-wl-admin')) {
        this.lclients.set(await Backend.POLICIES.getClients());
        this.form.addControl('client', new UntypedFormControl(this.lclients()?.[0]?.name));
      }
    } else {
      if (this.policy) {
        this.form.addControl('policy', new UntypedFormControl(this.policy.id));
      } else if (this.lallPolicies) {
        if (this.keycloak.hasRealmRole('bo-dip-wl-admin')) {
          this.lclients.set(await Backend.POLICIES.getClients());
          this.form.addControl('client', new UntypedFormControl(this.lclients()?.[0]?.name));
          this.patchListPolicyByClients(this.lclients()?.[0]?.name, this.lallPolicies);
          this.form.addControl('policy', new UntypedFormControl(this.listPoliciesClientSelected()?.[0]?.id));
        }
      } else {
        this.form.addControl('policy', new UntypedFormControl(''));
      }
      if (this.policy) {
        this.lApplications.set(await Backend.POLICIES.getAllApplication(this.policy));
      } else {
        this.lApplications.set(await Backend.POLICIES.getAllApplication(this.findPolicy()));
      }
      this.form.addControl('application', new UntypedFormControl(this.lApplications()?.[0]?.id));
      this.form.addControl('stampdebut', new UntypedFormControl(''));
      this.form.addControl('stampfin', new UntypedFormControl(''));
      this.number_value = 0;
    }
  }

  patchListPolicyByClients(client: string, lpolicies: Policy[]) {
    this.listPoliciesClientSelected.set(Backend.POLICIES.getPolicyByClient(client, lpolicies));
    if (this.form.get('policy')) {
      this.form.patchValue({ 'policy': this.listPoliciesClientSelected()?.[0]?.id });
    }
  }

  async setOs(os: string) {
    this.form.patchValue({ os: os });
    await this.getBWVersions(this.form.value['os']);
    this.setBwv(this.lbwversion()?.[0]?.version);
  }

  async setPolicy(policy: Policy) {
    this.form.patchValue({ policy: policy.id });
    this.lApplications.set(await Backend.POLICIES.getAllApplication(this.findPolicy()));
    this.setApp(this.lApplications()?.[0]);
  }

  setBwv(v: string) {
    this.form.patchValue({ BWVersion: v });
  }

  setClient(name: string) {
    this.form.patchValue({ client: name });
  }

  setApp(app: ClientApplication) {
    this.form.patchValue({ application: app.id });
  }

  openFullScreen({ content }: { content: any }) {
    this.modalrefFullScreen = this.modalService.open(content, { fullscreen: true, scrollable: true });
  }

  modaldismiss(action: string) {
    this.modalrefFullScreen?.close(action);
    this.modalrefcenteredScreen?.close(action);
    this.resetForm();
  }

  async createNewPolicy() {
    await this.getBWVersions(this.form.value['os']);
    this.newPolicy = {
      name: this.form.value["policy-name"],
      bwVersion: this.lbwversion()?.find(elem => elem.version == this.form.value["BWVersion"]),
    };
    this.nameClient = this.form.value['client'];
  }

  addPolicySaved(event: { policy: Policy, toast: string, nameClient?: string }) {
    this.addPolicy.emit(event);
    this.modaldismiss('close');
  }

  async resetForm() {
    if (this.addpolicy) {
      this.form.patchValue({ 'os': 'Android' });
      await this.getBWVersions(this.form.value['os']);
      this.form.patchValue({
        'policy-name': '',
        'BWVersion': this.lbwversion()?.[0]?.version
      });
      if (this.keycloak.hasRealmRole('bo-dip-wl-admin')) {
        this.lclients.set(await Backend.POLICIES.getClients());
        this.form.patchValue({ 'client': this.lclients()?.[0]?.name });
      }
    } else {
      if (this.policy) {
        this.form.patchValue({ 'policy': this.policy.id });
      } else if (this.lallPolicies) {
        if (this.keycloak.hasRealmRole('bo-dip-wl-admin')) {
          this.lclients.set(await Backend.POLICIES.getClients());
          this.form.patchValue({ 'client': this.lclients()?.[0]?.name });
          this.patchListPolicyByClients(this.lclients()?.[0]?.name, this.lallPolicies);
          this.form.patchValue({ 'policy': this.listPoliciesClientSelected()?.[0]?.id });
        }

      }
      this.lApplications.set(await Backend.POLICIES.getAllApplication(this.findPolicy()));
      this.form.patchValue({
        'application': this.lApplications()?.[0]?.id,
        'stampdebut': '',
        'stampfin': ''
      });
      this.range_disabled = true;
      this.number_value = 0;
    }
    this.resultSimulation.set(undefined);
  }

  public formSimulationError() {
    return (this.range_disabled && this.number_value && this.number_value != 0) ||
      (this.form.value['stampdebut'] != "" && this.form.value['stampfin'] != "");
  }

  public setDate(ldates: number[]) {
    this.form.patchValue({ stampdebut: ldates[0], stampfin: ldates[1] });
  }

  public findPolicy() {
    if (this.policy) {
      return this.policy;
    } else if (this.lallPolicies && this.keycloak.hasRealmRole('bo-dip-wl-admin')) {
      return this.lallPolicies.find(elem => elem.id == this.form.value['policy']);
    } else {
      return this.listPoliciesClientSelected()?.find(elem => elem.id == this.form.value['policy']);
    }
  }

  public findAppli() {
    return this.lApplications()?.find(elem => elem.id == this.form.value['application']);
  }

  public today() {
    return Backend.FORMAT.today();
  }

  formatNumber(n: number) {
    // Utilise le service de traduction pour la locale si besoin
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0 }).format(n);
  }

  public requestSimulation() {
    let object: any;
    let request: RequestSimulation = {
      applicationId: this.form.value['application'],
    };
    if (this.form.value['stampdebut'] && this.form.value['stampfin']) {
      request.startDate = new Date(this.form.value['stampdebut']);
      request.endDate = new Date(this.form.value['stampfin']);
    }
    if (this.number_value != 0 && this.range_disabled === true) {
      request.nbAttestations = this.number_value;
    }
    if (this.form.value['policy']) {
      object = { id: this.form.value['policy'], body: request };
    }
    this.resultSimulation.set(object);
  }
}
